import type { WidgetDialogVariant, WidgetLauncherVariant } from '@vd/types';
import styles from './WidgetIllustrations.module.css';

/**
 * Small drawings of the widget on a page, for the choices an owner makes about
 * it: where its launcher sits (TOADO-595) and what visitors can do with it
 * (TOADO-596). Each is a browser page in the app's own colours, so it reads on
 * either theme, with the widget drawn in the accent. Decoration only: the text
 * beside each says the same, so they are hidden from screen readers. Motion is
 * gentle and stops for anyone who asks for less of it.
 */

/**
 * The page every drawing starts from: browser bar, the site's header, and some
 * content. Shared with the extension's drawings (CaptureIllustrations).
 */
export function Page({ width, height, header = true }: { width: number; height: number; header?: boolean }) {
  const right = width - 1.5;
  return (
    <>
      <rect className={styles.page} x={1.5} y={1.5} width={width - 3} height={height - 3} rx={6} />
      <path className={styles.bar} d={`M1.5 12 V7.5 A6 6 0 0 1 7.5 1.5 H${right - 6} A6 6 0 0 1 ${right} 7.5 V12 Z`} />
      <circle className={styles.dot} cx={8} cy={7} r={1.6} />
      <circle className={styles.dot} cx={13} cy={7} r={1.6} />
      <circle className={styles.dot} cx={18} cy={7} r={1.6} />
      {header && (
        <>
          <rect className={styles.ink} x={8} y={16} width={14} height={4} rx={2} />
          <rect className={styles.faint} x={28} y={17} width={10} height={2.5} rx={1.25} />
          <rect className={styles.faint} x={42} y={17} width={10} height={2.5} rx={1.25} />
          <line className={styles.rule} x1={1.5} x2={right} y1={24} y2={24} />
        </>
      )}
    </>
  );
}

/** Where the launcher sits, for Appearance > Launcher > Style. */
export function LauncherStyleArt({ variant }: { variant: WidgetLauncherVariant }) {
  return (
    <svg className={styles.art} viewBox="0 0 120 72" aria-hidden="true" focusable="false" data-testid={`launcher-style-art-${variant}`}>
      <Page width={120} height={72} />
      <rect className={styles.ink} x={9} y={31} width={44} height={4} rx={2} />
      <rect className={styles.faint} x={9} y={39} width={64} height={3} rx={1.5} />
      <rect className={styles.faint} x={9} y={45} width={56} height={3} rx={1.5} />
      <rect className={styles.block} x={9} y={52} width={36} height={13} rx={2.5} />
      {variant === 'button' && (
        <g className={styles.launcher}>
          <circle className={styles.accent} cx={104} cy={57} r={7.5} />
          <circle className={styles.onAccent} cx={104} cy={57} r={2.2} />
        </g>
      )}
      {variant === 'tab' && (
        <g className={styles.launcher}>
          <path className={styles.accent} d="M118.5 30 H112 A3 3 0 0 0 109 33 V51 A3 3 0 0 0 112 54 H118.5 Z" />
          <rect className={styles.onAccent} x={112.2} y={37} width={2.4} height={10} rx={1.2} />
        </g>
      )}
      {variant === 'inline' && (
        <g className={styles.launcher}>
          <rect className={styles.accent} x={88} y={14.5} width={23} height={7} rx={3.5} />
          <rect className={styles.onAccent} x={92} y={17.2} width={15} height={1.8} rx={0.9} />
        </g>
      )}
      {variant === 'hidden' && (
        <>
          <rect className={styles.ghost} x={79} y={29} width={33} height={21} rx={3} />
          <g className={styles.keys}>
            <rect className={styles.key} x={82} y={55} width={13} height={11} rx={2} />
            <text className={styles.keyText} x={88.5} y={63} textAnchor="middle">⇧</text>
            <rect className={styles.key} x={98} y={55} width={13} height={11} rx={2} />
            <text className={styles.keyText} x={104.5} y={63} textAnchor="middle">B</text>
          </g>
        </>
      )}
    </svg>
  );
}

/** The report form's insides at (x, y), `width` wide: a title, a field, and Send. */
function FormSketch({ x, y, width }: { x: number; y: number; width: number }) {
  const inner = width - 10;
  return (
    <>
      <rect className={styles.ink} x={x + 5} y={y + 5} width={inner * 0.55} height={3} rx={1.5} />
      <rect className={styles.field} x={x + 5} y={y + 12} width={inner} height={11} rx={1.5} />
      <rect className={styles.accent} x={x + 5} y={y + 27} width={inner} height={6} rx={1.5} />
    </>
  );
}

/**
 * How the report form opens, for Appearance > Dialog > Presentation: a modal
 * in the middle over a dimmed page, or a panel beside the launcher with the
 * page left as it is.
 */
export function DialogStyleArt({ variant }: { variant: WidgetDialogVariant }) {
  return (
    <svg className={styles.art} viewBox="0 0 120 72" aria-hidden="true" focusable="false" data-testid={`dialog-style-art-${variant}`}>
      <Page width={120} height={72} />
      <rect className={styles.ink} x={9} y={31} width={44} height={4} rx={2} />
      <rect className={styles.faint} x={9} y={39} width={64} height={3} rx={1.5} />
      <rect className={styles.faint} x={9} y={45} width={56} height={3} rx={1.5} />
      <rect className={styles.block} x={9} y={52} width={36} height={13} rx={2.5} />
      <circle className={styles.accent} cx={106} cy={61} r={5.5} />
      <circle className={styles.onAccent} cx={106} cy={61} r={1.7} />
      {variant === 'modal' ? (
        <>
          {/* The page behind is dimmed and cannot be used until it closes. */}
          <path className={styles.dim} d="M1.5 12 H118.5 V64.5 A6 6 0 0 1 112.5 70.5 H7.5 A6 6 0 0 1 1.5 64.5 Z" />
          <rect className={styles.panel} x={36} y={20} width={48} height={40} rx={3} />
          <FormSketch x={36} y={22} width={48} />
        </>
      ) : (
        <>
          {/* Beside the launcher, with the page still there to use. */}
          <rect className={styles.panel} x={66} y={14} width={46} height={40} rx={3} />
          <FormSketch x={66} y={16} width={46} />
        </>
      )}
    </svg>
  );
}

/** An edge tab on the right edge, its top at `y`: the shape LauncherStyleArt draws. */
function tabPath(y: number): string {
  return `M118.5 ${y} H112 A3 3 0 0 0 109 ${y + 3} V${y + 19} A3 3 0 0 0 112 ${y + 22} H118.5 Z`;
}

/**
 * Whether visitors can drag the launcher, for Appearance > Launcher (t-hM_nVp).
 * On: the button carried from where the owner put it to where the visitor
 * drops it, or for an edge tab, slid up its edge. Off: the launcher at its
 * place, locked there.
 */
export function DragArt({ on, tab = false }: { on: boolean; tab?: boolean }) {
  return (
    <svg className={styles.art} viewBox="0 0 120 72" aria-hidden="true" focusable="false" data-testid="launcher-drag-art" data-on={on} data-tab={tab}>
      <Page width={120} height={72} />
      <rect className={styles.ink} x={9} y={31} width={44} height={4} rx={2} />
      <rect className={styles.faint} x={9} y={39} width={64} height={3} rx={1.5} />
      <rect className={styles.faint} x={9} y={45} width={56} height={3} rx={1.5} />
      <rect className={styles.block} x={9} y={52} width={36} height={13} rx={2.5} />
      {tab ? (on ? (
        <>
          {/* Where the owner put it, and the one way it moves: along its edge. */}
          <path className={styles.ghost} d={tabPath(44)} />
          <path className={styles.trail} d="M102 22 V58 M99 25 L102 21.5 L105 25 M99 55 L102 58.5 L105 55" />
          <g className={styles.slid}>
            <path className={styles.accent} d={tabPath(16)} />
            <rect className={styles.onAccent} x={112.2} y={22} width={2.4} height={10} rx={1.2} />
          </g>
        </>
      ) : (
        <>
          <path className={styles.accent} d={tabPath(44)} />
          <rect className={styles.onAccent} x={112.2} y={50} width={2.4} height={10} rx={1.2} />
          <g className={styles.lock}>
            <path className={styles.shackle} d="M98.5 51 v-2.5 a3 3 0 0 1 6 0 v2.5" />
            <rect className={styles.lockBody} x={96.5} y={51} width={10} height={8} rx={1.6} />
          </g>
        </>
      )) : on ? (
        <>
          {/* Where the owner put it, and the way the visitor took it. */}
          <circle className={styles.ghost} cx={104} cy={57} r={7.5} />
          <path className={styles.trail} d="M97 53 Q 86 36 78 37" />
          <g className={styles.dragged}>
            <circle className={styles.accent} cx={72} cy={36} r={7.5} />
            <circle className={styles.onAccent} cx={72} cy={36} r={2.2} />
            <path className={styles.cursor} d="M74 38 v13 l3.5 -3.3 l2.5 5.5 l2.4 -1.1 l-2.5 -5.4 l4.8 -0.2 z" />
          </g>
        </>
      ) : (
        <>
          <circle className={styles.accent} cx={104} cy={57} r={7.5} />
          <circle className={styles.onAccent} cx={104} cy={57} r={2.2} />
          {/* A small padlock beside it: it stays where you put it. */}
          <g className={styles.lock}>
            <path className={styles.shackle} d="M88.5 49 v-2.5 a3 3 0 0 1 6 0 v2.5" />
            <rect className={styles.lockBody} x={86.5} y={49} width={10} height={8} rx={1.6} />
          </g>
        </>
      )}
    </svg>
  );
}

/**
 * Whether the launcher shows on phones, for Appearance > Launcher (t-Wfbiva):
 * the same site on a computer and on a phone, the phone with the launcher or
 * without it.
 */
export function MobileArt({ on }: { on: boolean }) {
  return (
    <svg className={styles.art} viewBox="0 0 120 72" aria-hidden="true" focusable="false" data-testid="launcher-mobile-art" data-on={on}>
      <Page width={80} height={72} />
      <rect className={styles.ink} x={9} y={31} width={36} height={4} rx={2} />
      <rect className={styles.faint} x={9} y={39} width={52} height={3} rx={1.5} />
      <rect className={styles.faint} x={9} y={45} width={44} height={3} rx={1.5} />
      <rect className={styles.block} x={9} y={52} width={30} height={13} rx={2.5} />
      <circle className={styles.accent} cx={68} cy={59} r={5.5} />
      <circle className={styles.onAccent} cx={68} cy={59} r={1.7} />

      {/* The phone. */}
      <rect className={styles.page} x={88} y={4} width={29} height={64} rx={5} />
      <rect className={styles.dot} x={98} y={7.5} width={9} height={2} rx={1} />
      <rect className={styles.ink} x={92} y={16} width={14} height={3} rx={1.5} />
      <rect className={styles.faint} x={92} y={23} width={21} height={2.5} rx={1.25} />
      <rect className={styles.faint} x={92} y={28} width={17} height={2.5} rx={1.25} />
      <rect className={styles.block} x={92} y={34} width={21} height={14} rx={2} />
      {on ? (
        <g className={styles.launcher}>
          <circle className={styles.accent} cx={109} cy={59} r={4.5} />
          <circle className={styles.onAccent} cx={109} cy={59} r={1.4} />
        </g>
      ) : (
        <>
          <circle className={styles.ghost} cx={109} cy={59} r={4.5} />
          <line className={styles.strike} x1={105.5} y1={62.5} x2={112.5} y2={55.5} />
        </>
      )}
    </svg>
  );
}

/** A pointer whose tip is at (x, y). */
export function Cursor({ x, y }: { x: number; y: number }) {
  return (
    <path
      className={styles.cursor}
      d={`M${x} ${y} v13 l3.5 -3.3 l2.5 5.5 l2.4 -1.1 l-2.5 -5.4 l4.8 -0.2 z`}
    />
  );
}

/** A microphone centred on (x, y), about 12 units tall. */
function Mic({ x, y, className }: { x: number; y: number; className?: string }) {
  return (
    <g className={className}>
      <rect x={x - 2.5} y={y - 6} width={5} height={8} rx={2.5} />
      <path d={`M${x - 4.5} ${y - 1} a4.5 4.5 0 0 0 9 0 M${x} ${y + 3.5} v2.5 M${x - 2.5} ${y + 6} h5`} fill="none" />
    </g>
  );
}

/**
 * A visitor recording the page, for Capture > Recording (Jason, 10 Oct): the
 * page being recorded, the visitor moving through it and dropping a numbered
 * pin on each problem, and the recording's timeline filling as they go. At
 * rest (and for reduced motion) it shows the finished walkthrough.
 */
export function RecordingArt() {
  return (
    <svg className={styles.art} viewBox="0 0 160 100" aria-hidden="true" focusable="false" data-testid="recording-art">
      <Page width={160} height={100} header={false} />
      <rect className={styles.ink} x={10} y={18} width={46} height={5} rx={2.5} />
      <rect className={styles.faint} x={10} y={28} width={70} height={3} rx={1.5} />
      <rect className={styles.faint} x={10} y={34} width={60} height={3} rx={1.5} />
      <rect className={styles.block} x={10} y={44} width={66} height={34} rx={3} />
      <rect className={styles.block} x={86} y={34} width={64} height={22} rx={3} />
      <rect className={styles.faint} x={86} y={62} width={56} height={3} rx={1.5} />
      <rect className={styles.faint} x={86} y={68} width={48} height={3} rx={1.5} />

      {/* Recording. */}
      <rect className={styles.recBadge} x={114} y={14} width={38} height={13} rx={6.5} />
      <circle className={styles.recDot} cx={122} cy={20.5} r={3} />
      <text className={styles.recText} x={128} y={23.3}>REC</text>

      {/* A pin on each problem, in the order the visitor found them. */}
      <g className={styles.pinOne}>
        <circle className={styles.accent} cx={40} cy={56} r={6} />
        <text className={styles.pinText} x={40} y={58.6} textAnchor="middle">1</text>
      </g>
      <g className={styles.pinTwo}>
        <circle className={styles.accent} cx={128} cy={41} r={6} />
        <text className={styles.pinText} x={128} y={43.6} textAnchor="middle">2</text>
      </g>
      <g className={styles.recordTour}>
        <Cursor x={134} y={46} />
      </g>

      {/* The recording's timeline, with where each pin fell. */}
      <rect className={styles.track} x={10} y={86} width={140} height={5} rx={2.5} />
      <rect className={`${styles.progress} ${styles.grow}`} x={10} y={86} width={140} height={5} rx={2.5} />
      <rect className={`${styles.onAccent} ${styles.pinOne}`} x={44} y={86} width={2} height={5} />
      <rect className={`${styles.onAccent} ${styles.pinTwo}`} x={96} y={86} width={2} height={5} />
    </svg>
  );
}

/**
 * A visitor pointing at the page, for Capture > Pointing (Jason, 10 Oct): the
 * widget outlines whatever is under the pointer as it moves, a click picks
 * one, and the short form opens beside it. At rest (and for reduced motion)
 * it shows the element picked and the form open.
 */
export function PointingArt() {
  return (
    <svg className={styles.art} viewBox="0 0 160 100" aria-hidden="true" focusable="false" data-testid="pointing-art">
      <Page width={160} height={100} header={false} />
      {/* The widget's hint while pointing. */}
      <rect className={styles.hintPill} x={46} y={15} width={68} height={9} rx={4.5} />
      <rect className={styles.hintText} x={54} y={18.5} width={52} height={2} rx={1} />

      <rect className={styles.ink} x={10} y={32} width={50} height={5} rx={2.5} />
      <rect className={styles.block} x={10} y={44} width={64} height={44} rx={3} />
      <rect className={styles.blockStrong} x={88} y={32} width={56} height={14} rx={3} />
      <rect className={styles.faint} x={88} y={54} width={62} height={3} rx={1.5} />
      <rect className={styles.faint} x={88} y={60} width={52} height={3} rx={1.5} />

      {/* What the pointer is over, outlined as it moves. */}
      <rect className={`${styles.outline} ${styles.hoverFirst}`} x={7} y={41} width={70} height={50} rx={4} />
      <rect className={`${styles.outline} ${styles.hoverSecond}`} x={85} y={29} width={62} height={20} rx={4} />
      {/* Picked: a solid outline, and the form opens beside it. */}
      <g className={styles.picked}>
        <rect className={styles.outlineChosen} x={85} y={29} width={62} height={20} rx={4} />
        <rect className={styles.panel} x={92} y={54} width={58} height={38} rx={3} />
        <rect className={styles.ink} x={97} y={59} width={28} height={3} rx={1.5} />
        <rect className={styles.field} x={97} y={65} width={48} height={11} rx={1.5} />
        <rect className={styles.accent} x={97} y={80} width={48} height={6} rx={1.5} />
      </g>
      <g className={styles.pointTour}>
        <Cursor x={118} y={40} />
      </g>
    </svg>
  );
}

export type VoiceChoice = 'optional' | 'off';

/**
 * What visitors' voices do, for Capture > Recording > Visitors' voices: the
 * recording bar with its microphone the visitor may switch on and talk over,
 * their words written out beside the clip; or the page alone, the microphone
 * never offered.
 */
export function VoiceArt({ mode }: { mode: VoiceChoice }) {
  return (
    <svg className={styles.art} viewBox="0 0 120 72" aria-hidden="true" focusable="false" data-testid={`voice-art-${mode}`}>
      <Page width={120} height={72} />
      <rect className={styles.ink} x={9} y={31} width={44} height={4} rx={2} />
      <rect className={styles.faint} x={9} y={39} width={64} height={3} rx={1.5} />
      <rect className={styles.block} x={9} y={46} width={36} height={12} rx={2.5} />
      {/* The recording bar. */}
      <rect className={styles.panel} x={28} y={58} width={64} height={11} rx={5.5} />
      <circle className={styles.recDot} cx={35} cy={63.5} r={2.4} />
      <rect className={styles.faint} x={41} y={62.2} width={14} height={2.6} rx={1.3} />
      {mode === 'optional' ? (
        <>
          <Mic x={66} y={63.5} className={styles.micOn} />
          <g className={styles.waves}>
            <path className={styles.wave} d="M72 60.5 q2 3 0 6 M75 58.5 q3.5 5 0 10" />
          </g>
          {/* What they said, written out beside the clip. */}
          <g className={styles.speech}>
            <rect className={styles.bubble} x={70} y={30} width={42} height={20} rx={4} />
            <path className={styles.bubble} d="M80 49.5 l-3 5 l7 -5 z" />
            <rect className={styles.faint} x={75} y={35} width={32} height={2.5} rx={1.25} />
            <rect className={styles.faint} x={75} y={40} width={26} height={2.5} rx={1.25} />
            <rect className={styles.faint} x={75} y={45} width={18} height={2.5} rx={1.25} />
          </g>
        </>
      ) : (
        <>
          <Mic x={66} y={63.5} className={styles.micOff} />
          {/* A gap in the card's colour, then the slash in red, so "no
              microphone" reads at a glance (TOADO-836). */}
          <line className={styles.micStrikeGap} x1={60.5} y1={68.5} x2={71.5} y2={57.5} />
          <line className={styles.micStrike} x1={60.5} y1={68.5} x2={71.5} y2={57.5} />
          {/* The page only: a frame around what is recorded. */}
          <rect className={styles.ghost} x={74} y={28} width={38} height={24} rx={2} />
          <path className={styles.accent} d="M90 36 l7 4 l-7 4 z" />
        </>
      )}
    </svg>
  );
}

/**
 * Whether the widget is on, for Install (Jason, 10 Oct): the launcher on the
 * page, taking reports; or no launcher at all.
 */
export function WidgetOnArt({ on }: { on: boolean }) {
  return (
    <svg className={styles.art} viewBox="0 0 120 72" aria-hidden="true" focusable="false" data-testid="widget-on-art" data-on={on}>
      <Page width={120} height={72} />
      <rect className={styles.ink} x={9} y={31} width={44} height={4} rx={2} />
      <rect className={styles.faint} x={9} y={39} width={64} height={3} rx={1.5} />
      <rect className={styles.faint} x={9} y={45} width={56} height={3} rx={1.5} />
      <rect className={styles.block} x={9} y={52} width={36} height={13} rx={2.5} />
      {on ? (
        <>
          <rect className={styles.panel} x={70} y={20} width={42} height={30} rx={3} />
          <FormSketch x={70} y={20} width={42} />
          <g className={styles.launcher}>
            <circle className={styles.accent} cx={104} cy={60} r={6.5} />
            <circle className={styles.onAccent} cx={104} cy={60} r={2} />
          </g>
        </>
      ) : (
        <>
          <circle className={styles.ghost} cx={104} cy={60} r={6.5} />
          <line className={styles.strike} x1={99.5} y1={64.5} x2={108.5} y2={55.5} />
        </>
      )}
    </svg>
  );
}

/**
 * Team mode, for Install (Jason, 10 Oct). On: the report comes from someone on
 * your team, known by name and sent straight through. Off: from an anonymous
 * visitor, who passes the captcha first.
 */
export function TeamModeArt({ on }: { on: boolean }) {
  return (
    <svg className={styles.art} viewBox="0 0 120 72" aria-hidden="true" focusable="false" data-testid="team-mode-art" data-on={on}>
      <Page width={120} height={72} />
      <rect className={styles.ink} x={9} y={31} width={36} height={4} rx={2} />
      <rect className={styles.faint} x={9} y={39} width={48} height={3} rx={1.5} />
      <rect className={styles.block} x={9} y={46} width={36} height={18} rx={2.5} />
      <rect className={styles.panel} x={60} y={16} width={52} height={46} rx={3} />
      {on ? (
        <>
          {/* Someone you know, labelled as your team. */}
          <circle className={styles.accent} cx={68} cy={25} r={4.5} />
          <rect className={styles.ink} x={75} y={22} width={18} height={3} rx={1.5} />
          <rect className={styles.teamTag} x={75} y={27} width={16} height={4.5} rx={2.25} />
          <rect className={styles.field} x={65} y={36} width={42} height={12} rx={1.5} />
          <rect className={styles.accent} x={65} y={52} width={42} height={6} rx={1.5} />
        </>
      ) : (
        <>
          {/* An anonymous visitor, and the captcha first. */}
          <circle className={styles.visitor} cx={68} cy={25} r={4.5} />
          <rect className={styles.faint} x={75} y={23.5} width={18} height={3} rx={1.5} />
          <rect className={styles.field} x={65} y={34} width={42} height={9} rx={1.5} />
          <rect className={styles.captcha} x={65} y={45.5} width={42} height={5} rx={1} />
          <rect className={styles.captchaBox} x={67} y={46.5} width={3} height={3} rx={0.5} />
          <rect className={styles.accent} x={65} y={53} width={42} height={5} rx={1.5} />
        </>
      )}
    </svg>
  );
}

export type ReportWay = 'tell' | 'point' | 'record';

/** What a visitor does with the widget, for the setup wizard's "What people can do". */
export function ReportWayArt({ way }: { way: ReportWay }) {
  return (
    <svg className={styles.art} viewBox="0 0 160 100" aria-hidden="true" focusable="false" data-testid={`report-way-art-${way}`}>
      <Page width={160} height={100} />
      <rect className={styles.ink} x={10} y={32} width={52} height={5} rx={2.5} />
      <rect className={styles.faint} x={10} y={42} width={74} height={3} rx={1.5} />
      <rect className={styles.faint} x={10} y={48} width={64} height={3} rx={1.5} />
      <rect className={styles.block} x={10} y={58} width={64} height={30} rx={3} />
      {way === 'tell' && (
        <g>
          <rect className={styles.panel} x={94} y={30} width={58} height={62} rx={5} />
          <rect className={styles.ink} x={100} y={36} width={30} height={4} rx={2} />
          <rect className={styles.field} x={100} y={45} width={46} height={18} rx={2} />
          <rect className={styles.caret} x={104} y={49} width={1.5} height={9} rx={0.75} />
          <rect className={styles.field} x={100} y={67} width={46} height={7} rx={2} />
          <rect className={styles.accent} x={100} y={78} width={46} height={8} rx={2} />
        </g>
      )}
      {way === 'point' && (
        <g>
          <rect className={styles.outline} x={7} y={55} width={70} height={36} rx={4} />
          <g className={styles.pointer}>
            <path className={styles.cursor} d="M80 70 v17 l4.6 -4.3 l3.2 7.2 l3.1 -1.4 l-3.2 -7.1 l6.3 -0.2 z" />
          </g>
          <rect className={styles.panelGhost} x={104} y={34} width={48} height={30} rx={4} />
          <rect className={styles.faint} x={110} y={41} width={30} height={3} rx={1.5} />
          <rect className={styles.faint} x={110} y={48} width={36} height={3} rx={1.5} />
        </g>
      )}
      {way === 'record' && (
        <g>
          <rect className={styles.recBadge} x={112} y={30} width={38} height={13} rx={6.5} />
          <circle className={styles.recDot} cx={120} cy={36.5} r={3} />
          <text className={styles.recText} x={126} y={39.3}>REC</text>
          <g>
            <circle className={styles.accent} cx={70} cy={36} r={6} />
            <text className={styles.pinText} x={70} y={38.6} textAnchor="middle">1</text>
          </g>
          <g>
            <circle className={styles.accent} cx={44} cy={70} r={6} />
            <text className={styles.pinText} x={44} y={72.6} textAnchor="middle">2</text>
          </g>
          <rect className={styles.track} x={92} y={84} width={60} height={6} rx={3} />
          <rect className={styles.progress} x={92} y={84} width={38} height={6} rx={3} />
        </g>
      )}
    </svg>
  );
}

/**
 * How the widget works, in three steps (Jason, 16 Oct; the widget settings'
 * Learn more): 1, a visitor on your site opens the launcher; 2, they point
 * at what is wrong (or record it) and send; 3, it lands on your project's
 * board as a ticket. One drawing per step, so the steps stack on a narrow
 * page; the captions beside them say the same.
 */
export function WidgetFlowArt({ step }: { step: 1 | 2 | 3 }) {
  return (
    <svg className={styles.art} viewBox="0 0 120 80" aria-hidden="true" focusable="false" data-testid={`widget-flow-art-${step}`}>
      <Page width={120} height={80} />
      {step === 1 && <>
        <rect className={styles.ink} x={9} y={31} width={48} height={4} rx={2} />
        <rect className={styles.faint} x={9} y={39} width={70} height={3} rx={1.5} />
        <rect className={styles.faint} x={9} y={45} width={60} height={3} rx={1.5} />
        <rect className={styles.block} x={9} y={53} width={40} height={16} rx={2.5} />
        <g className={styles.launcher}>
          <circle className={styles.accent} cx={104} cy={64} r={8} />
          <circle className={styles.onAccent} cx={104} cy={64} r={2.4} />
        </g>
        <Cursor x={98} y={58} />
      </>}
      {step === 2 && <>
        <rect className={styles.faint} x={9} y={31} width={50} height={3} rx={1.5} />
        <rect className={styles.blockStrong} x={9} y={39} width={44} height={14} rx={2.5} />
        <rect className={styles.ghost} x={7} y={37} width={48} height={18} rx={3.5} />
        <g>
          <path className={styles.accent} d="M54 33 a5 5 0 1 1 6 0 l-3 5 z" />
          <circle className={styles.onAccent} cx={57} cy={30} r={1.6} />
        </g>
        <rect className={styles.page} x={66} y={30} width={46} height={42} rx={4} />
        <rect className={styles.ink} x={71} y={36} width={26} height={3.5} rx={1.75} />
        <rect className={styles.faint} x={71} y={43} width={36} height={3} rx={1.5} />
        <rect className={styles.faint} x={71} y={49} width={30} height={3} rx={1.5} />
        <rect className={styles.accent} x={87} y={60} width={20} height={7} rx={2} />
      </>}
      {step === 3 && <>
        {[9, 46, 83].map((x, column) => (
          <g key={x}>
            <rect className={styles.block} x={x} y={29} width={30} height={44} rx={3} />
            <rect className={styles.ink} x={x + 4} y={33} width={14} height={3} rx={1.5} />
            {column === 0
              ? <>
                <rect className={styles.page} x={x + 3} y={40} width={24} height={12} rx={2} />
                <rect className={styles.ghost} x={x + 1.5} y={38.5} width={27} height={15} rx={3} />
                <rect className={styles.accent} x={x + 6} y={44} width={12} height={3} rx={1.5} />
              </>
              : <rect className={styles.page} x={x + 3} y={40} width={24} height={10} rx={2} />}
            <rect className={styles.page} x={x + 3} y={column === 0 ? 56 : 53} width={24} height={10} rx={2} />
          </g>
        ))}
      </>}
    </svg>
  );
}
